import React, { useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography
} from '@mui/material';
import { Add as AddIcon, Cancel as CancelIcon } from '@mui/icons-material';
import PersonaForm, { type PersonaFormHandle } from '../PersonaDetailDialog/PersonaForm';

import type { I18nPersonasNs, I18nCommonNs } from '@/i18n/dictionaries/interfaces';
import { I18nNamespace } from '@/i18n/types/i18nNamespace';
import { useTranslation } from 'react-i18next';

import type { PersonaDto } from '@/api-gen/api.schemas';

interface CreatePersonaDialogProps {
  open: boolean;
  onClose: () => void;
  onRefresh?: (createdPersona?: PersonaDto) => void;
  defaultLanguageCode?: string;
}

const CreatePersonaDialog: React.FC<CreatePersonaDialogProps> = ({ open, onClose, onRefresh, defaultLanguageCode }) => {
  const { t: translate } = useTranslation(I18nNamespace.Personas);
  const { t: translateCommon } = useTranslation(I18nNamespace.Common);
  const personaFormRef = useRef<PersonaFormHandle>(null);

  const handleSave = (createdPersona?: PersonaDto) => {
    onRefresh?.(createdPersona);
    onClose();
  };

  const handleCancel = () => {
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Typography variant="h5">{translate(nameof.full<I18nPersonasNs>((n) => n.createDialog.title))}</Typography>
      </DialogTitle>
      
      <DialogContent dividers>
        <PersonaForm 
          ref={personaFormRef}
          mode="create"
          onSave={handleSave}
          onCancel={handleCancel}
          defaultLanguageCode={defaultLanguageCode}
        />
      </DialogContent>
      
      <DialogActions>
        <Button onClick={handleCancel} startIcon={<CancelIcon />}>
          {translateCommon(nameof.full<I18nCommonNs>((n) => n.buttonLabels.cancel))}
        </Button>
        <Button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            personaFormRef.current?.submit();
          }}
          variant="contained"
          startIcon={<AddIcon />}
        >
          {translate(nameof.full<I18nPersonasNs>((n) => n.createDialog.createPersona))}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreatePersonaDialog;